import api from '../../../utils/functions/api';
import { notifyOpportunityChanged } from '../../../utils/functions/opportunityChangeBus';

const BASE = process.env.REACT_APP_ACCOUNTING_API_URL;

// helper: ดึง error message ที่มีความหมายจาก axios error
function extractMessage(error) {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ'
  );
}

export const listOpportunities = ({
  status, isTender, contactId, ownerId, type, quotationDueDateFrom, quotationDueDateTo,
  includeAppointmentDates, q, page, size, sortBy, sortOrder, silent,
} = {}) => async () => {
  try {
    const { data } = await api.get(`${BASE}/marketing/opportunities`, {
      __silentError: silent,
      params: {
        status,
        is_tender: isTender,
        contact_id: contactId,
        owner_id: ownerId,
        type,
        quotation_due_date_from: quotationDueDateFrom,
        quotation_due_date_to: quotationDueDateTo,
        include_appointment_dates: includeAppointmentDates,
        q,
        page,
        size,
        sort_by: sortBy,
        sort_order: sortOrder,
      },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const getOpportunity = ({ id, silent }) => async () => {
  try {
    const { data } = await api.get(`${BASE}/marketing/opportunities/${id}`, { __silentError: silent });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const createOpportunity = ({
  projectName, contactId, contactNameSnapshot, detail, isTender, type, typeOther,
  value, remark, note, ownerId, orderedById, receivedById, code, xxx, budget, budgetApprove,
  quotationDueDate, appointments, status,
}) => async () => {
  try {
    const { data } = await api.post(`${BASE}/marketing/opportunities`, {
      code,
      xxx,
      status,
      project_name: projectName,
      contact_id: contactId,
      contact_name_snapshot: contactNameSnapshot,
      detail,
      is_tender: isTender,
      type,
      type_other: typeOther,
      value,
      budget,
      budget_approve: budgetApprove,
      quotation_due_date: quotationDueDate,
      remark,
      note,
      owner_id: ownerId,
      ordered_by: orderedById,
      received_by: receivedById,
      appointments,
    });
    notifyOpportunityChanged();
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const updateOpportunity = ({
  id, projectName, contactId, contactNameSnapshot, detail, isTender, type, typeOther,
  value, remark, note, poNumber, ownerId, orderedById, receivedById, createdBy, budget, budgetApprove,
  quotationDueDate, code, xxx, googleDriveLinks,
}) => async () => {
  try {
    const { data } = await api.patch(`${BASE}/marketing/opportunities/${id}`, {
      project_name: projectName,
      contact_id: contactId,
      contact_name_snapshot: contactNameSnapshot,
      detail,
      is_tender: isTender,
      type,
      type_other: typeOther,
      code,
      xxx,
      value,
      budget,
      budget_approve: budgetApprove,
      quotation_due_date: quotationDueDate,
      remark,
      note,
      po_number: poNumber,
      owner_id: ownerId,
      ordered_by: orderedById,
      received_by: receivedById,
      created_by: createdBy,
      google_drive_links: googleDriveLinks,
    });
    notifyOpportunityChanged();
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// เขียน project_id กลับเข้า opportunity — เรียกทันทีหลังกด "สร้างโครงการ" สำเร็จ (ดู
// useCreateProject.js) เพื่อให้ OpportunitiesTable.js รู้ว่า opportunity นี้ถูกสร้างเป็นโครงการ
// อะไรไปแล้ว โดยไม่ต้อง query ข้าม service ทุกครั้งที่เปิดตาราง
export const linkOpportunityProject = (id, projectId) => async () => {
  try {
    const { data } = await api.patch(`${BASE}/marketing/opportunities/${id}/link-project`, {
      project_id: projectId,
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const opportunityAddAppointment = ({ id, date, title }) => async () => {
  try {
    const { data } = await api.post(`${BASE}/marketing/opportunities/${id}/appointments`, { date, title });
    notifyOpportunityChanged();
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const opportunityRemoveAppointment = ({ id, appointmentId }) => async () => {
  try {
    await api.delete(`${BASE}/marketing/opportunities/${id}/appointments/${appointmentId}`);
    notifyOpportunityChanged();
    return true;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const deleteOpportunity = ({ id }) => async () => {
  try {
    await api.delete(`${BASE}/marketing/opportunities/${id}`);
    notifyOpportunityChanged();
    return true;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const changeOpportunityStatus = ({
  id, toStatus, note, poNumber, value,
}) => async () => {
  try {
    const { data } = await api.patch(`${BASE}/marketing/opportunities/${id}/status`, {
      to_status: toStatus,
      note,
      po_number: poNumber,
      value,
    });
    notifyOpportunityChanged();
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const uploadTenderDocuments = ({ opportunityId, docType, files }) => async () => {
  try {
    const formData = new FormData();
    formData.append('doc_type', docType);
    files.forEach((file) => formData.append('files', file));

    const { data } = await api.post(
      `${BASE}/marketing/opportunities/${opportunityId}/tender-documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const deleteTenderDocument = ({ id }) => async () => {
  try {
    await api.delete(`${BASE}/marketing/tender-documents/${id}`);
    return true;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// เช็คอินวันนี้ — upsert ฝั่ง backend เอง (ยิงซ้ำวันเดียวกัน = แก้ planNote แทนสร้างใหม่)
export const checkinOpportunity = ({ id, planNote }) => async () => {
  try {
    const { data } = await api.post(`${BASE}/marketing/opportunities/${id}/checkin`, { plan_note: planNote });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// เช็คอินของตัวเองวันนี้ทั้งหมด — ใช้บอกในหน้า "เช็กอิน" ว่าอันไหนเช็คอินไปแล้ว/ยัง
export const myTodayCheckins = ({ silent } = {}) => async () => {
  try {
    const { data } = await api.get(`${BASE}/marketing/checkins/my-today`, { __silentError: silent });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// ภาพรวมเช็คอินวันนี้ของทุกคน — ใช้ในแท็บ "ภาพรวมการทำงาน" มุมมอง "วันนี้"
export const todayCheckinsOverview = ({ silent } = {}) => async () => {
  try {
    const { data } = await api.get(`${BASE}/marketing/checkins/today`, { __silentError: silent });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// ประวัติเช็คอินย้อนหลังในช่วงวันที่ — ใช้ในแท็บ "ภาพรวมการทำงาน" มุมมอง "ประวัติ" (ตาราง matrix)
export const checkinHistory = ({ dateFrom, dateTo, employeeId, silent } = {}) => async () => {
  try {
    const { data } = await api.get(`${BASE}/marketing/checkins/history`, {
      __silentError: silent,
      params: { date_from: dateFrom, date_to: dateTo, employee_id: employeeId },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};
